import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import FolderOpenIcon from "@mui/icons-material/FolderOpen"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Chip from "@mui/material/Chip"
import Stack from "@mui/material/Stack"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useMemo, useRef, useState } from "react"

import type { LiveChatWidgetCdnObject } from "@models/live_chat_widget"
import { formatBytes } from "@utils/functions/format_bytes"

import ConfirmPublishDialog from "./confirm_publish_dialog"

export const WIDGET_ENTRY_FILE = "livechatWidget.js"

/** Mirrors the server's `stripCommonRootDirectory` so the preview matches the write. */
export function widgetRelativePath(file: File): string {
  const raw =
    (file as File & { webkitRelativePath?: string }).webkitRelativePath ||
    file.name
  return raw.replace(/\\/g, "/").replace(/^\/+/, "")
}

export function stripCommonRoot(paths: string[]): string[] {
  let current = [...paths]
  while (current.length && current.every((item) => item.includes("/"))) {
    const root = current[0].split("/")[0]
    if (!current.every((item) => item.split("/")[0] === root)) break
    current = current.map((item) => item.slice(root.length + 1))
  }
  return current
}

export default function PublishPanel({
  translate,
  busy,
  canWrite,
  liveFiles,
  prefix,
  onPublish
}: {
  translate: (key: string) => string
  busy: boolean
  canWrite: boolean
  liveFiles: LiveChatWidgetCdnObject[]
  prefix: string
  onPublish: (input: {
    files: File[]
    version?: string
    note?: string
  }) => Promise<boolean>
}) {
  const folderInputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>([])
  const [version, setVersion] = useState("")
  const [note, setNote] = useState("")
  const [confirmOpen, setConfirmOpen] = useState(false)

  const paths = useMemo(
    () => stripCommonRoot(files.map(widgetRelativePath)),
    [files]
  )
  const hasEntryFile = paths.includes(WIDGET_ENTRY_FILE)
  const totalBytes = files.reduce((total, file) => total + file.size, 0)

  // The orphan sweep the server will run, computed here so the operator sees
  // exactly which live objects the publish removes before they confirm.
  const removedKeys = useMemo(() => {
    const kept = new Set(paths.map((item) => `${prefix}${item}`))
    return liveFiles
      .map((object) => object.key)
      .filter((key) => key && key !== prefix && !kept.has(key))
  }, [liveFiles, paths, prefix])

  const pick = (input: HTMLInputElement | null) => input?.click()

  const onFilesSelected = (list: FileList | null) => {
    setFiles(list ? Array.from(list) : [])
  }

  const reset = () => {
    setFiles([])
    setVersion("")
    setNote("")
    if (folderInputRef.current) folderInputRef.current.value = ""
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const confirmPublish = async () => {
    const published = await onPublish({
      files,
      version: version.trim() || undefined,
      note: note.trim() || undefined
    })
    setConfirmOpen(false)
    if (published) reset()
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack gap={2}>
          <Typography variant="subtitle1">
            {translate("live_chat_widget_publish_title")}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {translate("live_chat_widget_publish_hint")}
          </Typography>

          <Stack direction="row" gap={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<FolderOpenIcon />}
              disabled={busy || !canWrite}
              onClick={() => pick(folderInputRef.current)}
            >
              {translate("live_chat_widget_pick_folder")}
            </Button>
            <Button
              variant="text"
              disabled={busy || !canWrite}
              onClick={() => pick(fileInputRef.current)}
            >
              {translate("live_chat_widget_pick_files")}
            </Button>
            {files.length > 0 && (
              <Button
                variant="text"
                color="secondary"
                disabled={busy}
                onClick={reset}
              >
                {translate("live_chat_widget_clear_selection")}
              </Button>
            )}
          </Stack>

          {/* `webkitdirectory` is not in React's HTML typings. */}
          <input
            ref={folderInputRef}
            type="file"
            multiple
            hidden
            {...{ webkitdirectory: "", directory: "" }}
            onChange={(event) => onFilesSelected(event.target.files)}
          />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(event) => onFilesSelected(event.target.files)}
          />

          {files.length > 0 && (
            <Stack gap={1}>
              <Stack
                direction="row"
                gap={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <Chip
                  size="small"
                  label={`${files.length} ${translate("live_chat_widget_file_count")}`}
                />
                <Chip size="small" label={formatBytes(totalBytes)} />
                {hasEntryFile ? (
                  <Chip
                    size="small"
                    color="success"
                    label={WIDGET_ENTRY_FILE}
                  />
                ) : (
                  <Chip
                    size="small"
                    color="error"
                    label={translate("live_chat_widget_entry_missing_chip")}
                  />
                )}
              </Stack>

              <Stack
                sx={{
                  maxHeight: "12rem",
                  overflowY: "auto",
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  p: 1
                }}
              >
                {paths.map((item, index) => (
                  <Typography
                    key={item}
                    variant="caption"
                    sx={{ fontFamily: "monospace" }}
                  >
                    {item} · {formatBytes(files[index]?.size)}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          )}

          {files.length > 0 && !hasEntryFile && (
            <Alert severity="error">
              {translate("live_chat_widget_entry_missing_error").replace(
                "{file}",
                WIDGET_ENTRY_FILE
              )}
            </Alert>
          )}

          <Stack direction="row" gap={2} flexWrap="wrap">
            <TextField
              size="small"
              label={translate("live_chat_widget_version_label")}
              placeholder="2026.09.20"
              value={version}
              onChange={(event) => setVersion(event.target.value)}
              disabled={busy || !canWrite}
              sx={{ minWidth: "14rem" }}
            />
            <TextField
              size="small"
              label={translate("live_chat_widget_note_label")}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              disabled={busy || !canWrite}
              sx={{ flex: 1, minWidth: "18rem" }}
            />
          </Stack>

          <Stack direction="row">
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              disabled={busy || !canWrite || !files.length || !hasEntryFile}
              onClick={() => setConfirmOpen(true)}
            >
              {translate("live_chat_widget_publish_action")}
            </Button>
          </Stack>
        </Stack>
      </CardContent>

      <ConfirmPublishDialog
        isOpen={confirmOpen}
        setClose={() => setConfirmOpen(false)}
        translate={translate}
        loading={busy}
        fileCount={files.length}
        totalBytes={totalBytes}
        removedKeys={removedKeys}
        onConfirm={confirmPublish}
      />
    </Card>
  )
}
